import React, { useCallback, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Typography,
  TextField,
  Box,
  styled,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  useFetchUsersWithOrganizationsQuery,
  useUpdateUsersActiveStatusMutation,
} from "../../services/profileService";

/**
 * @typedef {Object} UserOrganization
 * @property {number} organization_id
 * @property {string} organization_name
 * @property {string} role
 */

/**
 * @typedef {Object} UserWithOrganizations
 * @property {string} email
 * @property {string} name
 * @property {boolean} active
 * @property {UserOrganization[]} organizations
 */

const INACTIVE_ROW_CLASS = "users-list-row-inactive";

const UsersList = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [togglingEmail, setTogglingEmail] = useState("");
  const [actionFeedback, setActionFeedback] = useState({ type: "", message: "" });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    row: null,
    nextActive: null,
  });

  const {
    data: usersPayload,
    isLoading,
    isError,
    error,
  } = useFetchUsersWithOrganizationsQuery(undefined, {
    skip: user?.role !== "superadmin",
  });

  const [updateUsersActiveStatus] = useUpdateUsersActiveStatusMutation();

  const users = useMemo(() => {
    const inner = usersPayload?.data;
    if (Array.isArray(inner)) return inner;
    if (Array.isArray(usersPayload)) return usersPayload;
    return [];
  }, [usersPayload]);

  const organizationOptions = useMemo(() => {
    const map = new Map();
    users.forEach((u) => {
      (u.organizations || []).forEach((o) => {
        if (o?.organization_id != null && o.organization_name) {
          map.set(o.organization_id, o.organization_name);
        }
      });
    });
    return [...map.entries()]
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
  }, [users]);

  const rows = useMemo(() => {
    return users.map((u, index) => ({
      id: u.email || `row-${index}`,
      name: u.name ?? "",
      email: u.email ?? "",
      active: u.active === true,
      organizations: u.organizations ?? [],
    }));
  }, [users]);

  const isTogglingStatus = Boolean(togglingEmail);

  const handleOpenConfirmDialog = useCallback((row) => {
    if (isTogglingStatus) return;

    setConfirmDialog({
      open: true,
      row,
      nextActive: !row.active,
    });
  }, [isTogglingStatus]);

  const handleCloseConfirmDialog = useCallback(() => {
    if (isTogglingStatus) return;

    setConfirmDialog({
      open: false,
      row: null,
      nextActive: null,
    });
  }, [isTogglingStatus]);

  const handleConfirmToggleActiveStatus = useCallback(async () => {
    const row = confirmDialog.row;
    const nextActive = confirmDialog.nextActive;
    const email = String(row?.email || "").trim();

    if (!email || nextActive == null || isTogglingStatus) return;

    const orgFilter =
      organizationId === "" ? null : Number.parseInt(String(organizationId), 10);

    setActionFeedback({ type: "", message: "" });
    setTogglingEmail(email);

    try {
      const payload = { email, active: nextActive };
      if (orgFilter != null && !Number.isNaN(orgFilter)) {
        payload.organization_id = orgFilter;
      }

      const result = await updateUsersActiveStatus(payload).unwrap();
      const responseData = result?.data ?? result;
      const message =
        (responseData && typeof responseData === "object" && responseData.message) ||
        `User ${nextActive ? "active" : "inactive"} successfully.`;

      setActionFeedback({ type: "success", message: String(message) });
      setConfirmDialog({
        open: false,
        row: null,
        nextActive: null,
      });
    } catch (err) {
      const message =
        (err && typeof err === "object" && err.message) ||
        (err && typeof err === "object" && err.data && err.data.message) ||
        "Failed to update user status. Please try again.";
      setActionFeedback({ type: "error", message: String(message) });
    } finally {
      setTogglingEmail("");
    }
  }, [
    confirmDialog.nextActive,
    confirmDialog.row,
    isTogglingStatus,
    organizationId,
    updateUsersActiveStatus,
  ]);

  const filteredRows = useMemo(() => {
    const q = String(searchQuery || "").trim().toLowerCase();
    const orgFilter =
      organizationId === "" ? null : Number.parseInt(String(organizationId), 10);

    return rows.filter((row) => {
      if (orgFilter != null && !Number.isNaN(orgFilter)) {
        const hasOrg = (row.organizations || []).some(
          (o) => o.organization_id === orgFilter
        );
        if (!hasOrg) return false;
      }
      if (!q) return true;
      const nameText = String(row.name || "").toLowerCase();
      const emailText = String(row.email || "").toLowerCase();
      return nameText.includes(q) || emailText.includes(q);
    });
  }, [rows, searchQuery, organizationId]);

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: "Name",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "email",
        headerName: "Email",
        flex: 1,
        minWidth: 220,
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 1,
        minWidth: 420,
        sortable: false,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          const isRowActive = params.row.active === true;
          const isRowToggling = togglingEmail === params.row.email;

          return (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
                width: "100%",
                height: "100%",
                flexWrap: "wrap",
              }}
            >
              <CustomBtn
                sx={{ background: "#1085AB", borderColor: "#1085AB" }}
                onClick={() =>
                  navigate(`/users/view/${encodeURIComponent(params.row.email)}`)
                }
              >
                View
              </CustomBtn>
              <CustomBtn
                onClick={() =>
                  navigate(`/users/edit/${encodeURIComponent(params.row.email)}`)
                }
              >
                Edit
              </CustomBtn>
              <CustomBtn
                disabled={isTogglingStatus}
                sx={
                  isRowActive
                    ? { background: "#6B7280", borderColor: "#6B7280" }
                    : { background: "#2E7D32", borderColor: "#2E7D32" }
                }
                onClick={() => handleOpenConfirmDialog(params.row)}
              >
                {isRowToggling ? "Updating…" : isRowActive ? "Inactive" : "Active"}
              </CustomBtn>
            </Box>
          );
        },
      },
    ],
    [handleOpenConfirmDialog, isTogglingStatus, navigate, togglingEmail]
  );

  const getRowClassName = useCallback(
    (params) => (params.row.active === true ? "" : INACTIVE_ROW_CLASS),
    []
  );

  if (user?.role !== "superadmin") {
    return <Navigate to="/view-org" replace />;
  }

  const errorMessage =
    (error && typeof error === "object" && error.message) ||
    (error && typeof error === "object" && error.data && error.data.message) ||
    "Unable to load users. Please try again.";

  return (
    <SectionWrap component="section">
      <ContainerWrap>
        <RowWrap sx={{ marginBottom: "10px" }}>
          <Col12Wrap>
            <TitleWrap sx={{ textTransform: "capitalize", marginBottom: "0" }}>
              Users
            </TitleWrap>
          </Col12Wrap>
        </RowWrap>

        {isError && (
          <RowWrap sx={{ marginBottom: "16px" }}>
            <Col12Wrap>
              <Alert severity="error">{String(errorMessage)}</Alert>
            </Col12Wrap>
          </RowWrap>
        )}

        {actionFeedback.message && (
          <RowWrap sx={{ marginBottom: "16px" }}>
            <Col12Wrap>
              <Alert
                severity={actionFeedback.type === "success" ? "success" : "error"}
                onClose={() => setActionFeedback({ type: "", message: "" })}
              >
                {actionFeedback.message}
              </Alert>
            </Col12Wrap>
          </RowWrap>
        )}

        <RowWrap sx={{ marginBottom: "20px", alignItems: "flex-end", gap: 2 }}>
          <Col6Wrap sx={{ flex: "1 1 280px", maxWidth: { md: "50%" } }}>
            <FormGroupWrap sx={{ maxWidth: "100%", marginLeft: 0 }}>
              <CustomTextField
                variant="outlined"
                placeholder="Filter by name or email"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth
              />
            </FormGroupWrap>
          </Col6Wrap>
          <Col6Wrap sx={{ flex: "1 1 260px", maxWidth: { md: "50%" } }}>
            <FormControl fullWidth size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="users-org-filter-label">Organization</InputLabel>
              <Select
                labelId="users-org-filter-label"
                id="users-org-filter"
                label="Organization"
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
              >
                <MenuItem value="">
                  <em>All organizations</em>
                </MenuItem>
                {organizationOptions.map((o) => (
                  <MenuItem key={o.id} value={String(o.id)}>
                    {o.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Col6Wrap>
        </RowWrap>

        <RowWrap>
          <Col12Wrap>
            <TableContentwrap>
              {isLoading ? (
                <Typography sx={{ p: 2 }}>Loading users…</Typography>
              ) : (
                <DataGrid
                  rows={filteredRows}
                  columns={columns}
                  getRowClassName={getRowClassName}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 30 },
                    },
                  }}
                  pageSizeOptions={[30, 50, 100]}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  localeText={{ noRowsLabel: "No users match the current filters" }}
                  sx={{
                    "& .MuiDataGrid-row": {
                      alignItems: "center",
                    },
                    "& .MuiDataGrid-cell": {
                      display: "flex",
                      alignItems: "center",
                      py: "10px",
                    },
                    "& .MuiDataGrid-cell[data-field='actions']": {
                      justifyContent: "center",
                    },
                    "& .MuiDataGrid-cell[data-field='actions'] .MuiDataGrid-cellContent": {
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                      height: "100%",
                      overflow: "visible",
                    },
                    [`& .${INACTIVE_ROW_CLASS}`]: {
                      backgroundColor: "rgba(0, 0, 0, 0.04)",
                    },
                    [`& .${INACTIVE_ROW_CLASS} .MuiDataGrid-cell:not([data-field="actions"])`]: {
                      opacity: 0.55,
                      color: "#6B7280",
                    },
                    "& .MuiDataGrid-iconButtonContainer": {
                      visibility: "visible !important",
                      width: "auto !important",
                      svg: {
                        opacity: "1 !important",
                      },
                    },
                  }}
                />
              )}
            </TableContentwrap>
          </Col12Wrap>
        </RowWrap>
      </ContainerWrap>

      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        maxWidth="xs"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "7px",
            padding: "10px",
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "20px", fontWeight: 600, textAlign: "center", pb: 1 }}>
          Confirm status change
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", color: "#2C2C2E" }}>
          {confirmDialog.nextActive === true ? (
            <Typography>
              Are you sure you want to activate{" "}
              <strong>{confirmDialog.row?.name || confirmDialog.row?.email || "this user"}</strong>
              ?
            </Typography>
          ) : (
            <Typography>
              Are you sure you want to Inactive{" "}
              <strong>{confirmDialog.row?.name || confirmDialog.row?.email || "this user"}</strong>
              ?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: "10px", pb: 2 }}>
          <CustomBtn
            variant="outlined"
            disabled={isTogglingStatus}
            onClick={handleCloseConfirmDialog}
            sx={{
              background: "#ffffff",
              color: "#2C2C2E",
              borderColor: "#DADADA",
              "&:hover": {
                background: "#f5f5f5",
                borderColor: "#DADADA",
              },
            }}
          >
            Cancel
          </CustomBtn>
          <CustomBtn
            disabled={isTogglingStatus}
            onClick={handleConfirmToggleActiveStatus}
            sx={
              confirmDialog.nextActive === true
                ? { background: "#2E7D32", borderColor: "#2E7D32", minWidth: "100px" }
                : { background: "#6B7280", borderColor: "#6B7280", minWidth: "100px" }
            }
          >
            {isTogglingStatus ? (
              <CircularProgress size={18} sx={{ color: "#ffffff" }} />
            ) : (
              "Confirm"
            )}
          </CustomBtn>
        </DialogActions>
      </Dialog>
    </SectionWrap>
  );
};

export default UsersList;

const SectionWrap = styled(Box)(() => ({
  padding: "30px 0",
}));
const ContainerWrap = styled(Box)(() => ({
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
  marginLeft: "auto",
  marginRight: "auto",
}));
const RowWrap = styled(Box)(() => ({
  display: "flex",
  flexWrap: "wrap",
  marginLeft: "-15px",
  marginRight: "-15px",
}));
const Col12Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "100%",
  paddingLeft: "15px",
  paddingRight: "15px",
}));
const Col6Wrap = styled(Box)(() => ({
  flex: "0 0 auto",
  width: "100%",
  maxWidth: "50%",
  paddingLeft: "15px",
  paddingRight: "15px",
  "@media (max-width: 991px)": {
    maxWidth: "100%",
    marginTop: "10px",
    marginBottom: "10px",
  },
}));
const TitleWrap = styled(Typography)(() => ({
  fontSize: "28px",
  fontWeight: "600",
  lineHeight: "1.445",
  color: "#000000",
  marginBottom: "15px",
}));
const TableContentwrap = styled(Box)(() => ({
  padding: "15px",
  background: "#ffffff",
  borderRadius: "7px",
  boxShadow: "0 0 5px 0 rgba(0, 0, 0, 0.05)",
}));
const FormGroupWrap = styled(Box)(() => ({
  maxWidth: "420px",
  marginLeft: "auto",
  marginBottom: "0px",
  position: "relative",
  "@media (max-width: 991px)": {
    maxWidth: "100%",
  },
}));
const CustomTextField = styled(TextField)(() => ({
  width: "99%",
  border: "1px solid #DADADA",
  background: "#ffffff",
  borderRadius: "7px",
  "& .MuiInputBase-input": {
    fontSize: "16px",
    fontWeight: "300",
    lineHeight: "1",
    color: "#333333",
    padding: "10px 20px",
  },
  "& fieldset": {
    border: "none",
  },
}));
const CustomBtn = styled(Button)(() => ({
  minWidth: "auto",
  fontSize: "14px",
  fontWeight: "400",
  lineHeight: "1",
  color: "#ffffff",
  textTransform: "capitalize",
  background: "#F28021",
  borderColor: "#F28021",
  borderRadius: "5px",
  padding: "8px 14px",
  margin: "0",
  boxShadow: "none !important",
}));
